import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, NgZone, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatLegacyDialogRef as MatDialogRef, MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA } from '@angular/material/legacy-dialog';
import { MatLegacyPaginator as MatPaginator } from '@angular/material/legacy-paginator';
import { MatSort } from '@angular/material/sort';
import { MatLegacyTable as MatTable, MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { TranslateService } from '@ngx-translate/core';
import { Subject, interval, merge, ReplaySubject, Observable, of, throwError } from 'rxjs';
import { debounce, map, flatMap, catchError, pairwise, startWith } from 'rxjs/operators';
import { LookupGroup, valueGroupOptions } from '../../../../shared/autocomplete';
import { ErrorMessageType } from '../../../../shared/enum';
import { updateStickyColumnStyles } from '../../../../shared/table';
import { preselectResetLookupValue, toggleEditMode } from '../../../../shared/utils';
import { CustomValidator } from '../../../../shared/validation';
import { IEuroTaxResult } from '../../../models/eurotaxresult';
import { IEuroTaxResultMake } from '../../../models/eurotaxresultmake';
import { ILookupValue } from '../../../models/lookupvalues';
import { EurotaxService } from '../../../service/eurotax.service';
import { LookupService } from '../../../service/lookup.service';
import { MessageService } from '../../../service/message.service';
import { PopupService } from '../../../service/popup.service';

@Component({
  selector: 'app-eurotax-list-dialog',
  templateUrl: './eurotax-list-dialog.component.html',
  styleUrls: ['./eurotax-list-dialog.component.scss']
})
export class EurotaxListDialogComponent implements OnInit {
  formGroup: UntypedFormGroup;

  displayedColumns: string[];
  dataSource = new MatTableDataSource<IEuroTaxResult>();

  marken = new ReplaySubject<Array<IEuroTaxResultMake>>();
  markeGroupOptions: Observable<LookupGroup[]>;

  modelsmarkebaujahr = new ReplaySubject<Array<IEuroTaxResult>>();
  models = new ReplaySubject<Array<IEuroTaxResult>>();
  modelGroupOptions: Observable<LookupGroup[]>;

  antriebsarten = new ReplaySubject<Array<ILookupValue>>();
  antriebsartenSelected = new ReplaySubject<ILookupValue>();
  hubraeume = new ReplaySubject<Array<ILookupValue>>();
  leistungen = new ReplaySubject<Array<ILookupValue>>();
  doors = new ReplaySubject<Array<ILookupValue>>();
  bauarten = new ReplaySubject<Array<ILookupValue>>();

  eurotaxresults = new Subject<IEuroTaxResult[]>();

  expertMode: boolean;
  motorrad: boolean;

  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort: MatSort;
  @ViewChild(MatTable, { static: true }) table: MatTable<IEuroTaxResult>;

  constructor(private fb: UntypedFormBuilder, public dialogRef: MatDialogRef<any>, @Inject(MAT_DIALOG_DATA) public data: any,
    private eurotaxService: EurotaxService, private ngZone: NgZone, public translateService: TranslateService, public popupService: PopupService,
    public lookupService: LookupService, private messageService: MessageService) {

    this.expertMode = this.data.expertMode;
    this.motorrad = this.data.motorrad;

    this.displayedColumns = ['Select', 'HandelsBezeichnung', 'Baujahr', 'Antriebsart', 'Hubraum', 'Leistung', 'Listenpreis']
      .filter(x => !this.motorrad || this.motorrad && x !== 'Baujahr');

    this.marken.subscribe((data) => {
      const values = data.map(x => ({ Id: x.makeNatCode.toString(), Key: x.makeNatCode.toString(), Value: x.makeName }));
      this.markeGroupOptions = valueGroupOptions(of(values).pipe(
        flatMap(data => data), map(data => ({ category: data.Value.substring(0, 1), value: data })),
        catchError((err: HttpErrorResponse) => { this.popupService.openErrorPopup(err.message); return throwError(err); })), this.marke);
      preselectResetLookupValue(this.marke, values, (): void => { }, (): void => { }, (): void => { });
    });
    this.modelsmarkebaujahr.subscribe((data) => {
      const models = this.motorrad ? Array.from(data.reduce(
        (m, e) => m.set(e.typBezeichnung, e),
        new Map()
      )
        .values())
        .sort((a, b) => (a.typBezeichnung < b.typBezeichnung ? -1 : 1))
        .map(x => ({ Id: x.typBezeichnung, Key: x.typBezeichnung, Value: x.typBezeichnung })) :
        Array.from(data.reduce(
          (m, e) => m.set(e.modNameGrp1, e),
          new Map()
        )
          .values())
          .sort((a, b) => (a.modNameGrp1 < b.modNameGrp1 ? -1 : 1))
          .map(x => ({ Id: x.modNameGrp1, Key: x.modNameGrp1, Value: x.modNameGrp1 }));
      this.modelGroupOptions = valueGroupOptions(of(models).pipe(
        flatMap(data => data), map(data => ({ category: data.Value.substring(0, 1), value: data })),
        catchError((err: HttpErrorResponse) => { this.popupService.openErrorPopup(err.message); return throwError(err); })), this.model);
      preselectResetLookupValue(this.model, models, (): void => { }, (): void => { }, (): void => { });
    });
    this.models.subscribe((data) => {
      this.antriebsarten.next([{ Id: null, Key: null, Value: null }].concat(Array.from(data.reduce(
        (m, e) => m.set(e.typtxtFuelTYPeCd2, e),
        new Map()
      )
        .values())
        .sort((a, b) => (a.typtxtFuelTYPeCd2TXTTextLong < b.typtxtFuelTYPeCd2TXTTextLong ? -1 : 1))
        .map(x => ({ Id: x.typtxtFuelTYPeCd2, Key: x.typtxtFuelTYPeCd2, Value: x.typtxtFuelTYPeCd2TXTTextLong }))));

      if (this.showHubraum) {
        this.hubraeume.next([{ Id: null, Key: null, Value: null }].concat(Array.from(data.reduce(
          (m, e) => m.set(e.typCapTech, e),
          new Map()
        )
          .values())
          .sort((a, b) => (a.typCapTech < b.typCapTech ? -1 : 1))
          .map(x => ({ Id: x.typCapTech.toString(), Key: x.typCapTech.toString(), Value: x.typCapTech.toString() }))));
      }

      this.leistungen.next([{ Id: null, Key: null, Value: null }].concat(Array.from(data.reduce(
        (m, e) => m.set(e.nennleistungKw.toString().concat(e.nennleistungPs.toString()), e),
        new Map()
      )
        .values())
        .sort((a, b) => (a.nennleistungKw + a.nennleistungPs < b.nennleistungKw + b.nennleistungPs ? -1 : 1))
        .map(x => ({
          Id: x.nennleistungKw.toString(),
          Key: x.nennleistungKw.toString(),
          Value: x.nennleistungKw.toString().concat('kW/').concat(x.nennleistungPs.toString()).concat('PS')
        }))));

      if (this.showTuerenBauart) {
        this.doors.next([{ Id: null, Key: null, Value: null }].concat(Array.from(data.reduce(
          (m, e) => m.set(e.typeDoor, e),
          new Map()
        )
          .values())
          .sort((a, b) => (a.typeDoor < b.typeDoor ? -1 : 1))
          .map(x => ({ Id: x.typeDoor.toString(), Key: x.typeDoor.toString(), Value: x.typeDoor.toString() }))));
        this.bauarten.next([{ Id: null, Key: null, Value: null }].concat(Array.from(data.reduce(
          (m, e) => m.set(e.typtxtBodyCo1Cd2, e),
          new Map()
        )
          .values())
          .sort((a, b) => (a.typtxtBodyCo1Cd2 < b.typtxtBodyCo1Cd2 ? -1 : 1))
          .map(x => ({ Id: x.typtxtBodyCo1Cd2, Key: x.typtxtBodyCo1Cd2, Value: x.typtxtBodyCo1Cd2 }))));
      }
    });
    this.antriebsarten.subscribe(data => {
      preselectResetLookupValue(this.antriebsart, data, (): void => { this.antriebsartenSelected.next(this.antriebsart.value); },
        (): void => { this.antriebsartenSelected.next(null); },
        (): void => { this.antriebsartenSelected.next(null); });
    });
    this.antriebsartenSelected.pipe(startWith(null as ILookupValue), pairwise()).subscribe(([prev, next]: [ILookupValue, ILookupValue]) => {
      if (next?.Id && prev?.Id !== next?.Id && next.Id === '00100004') {
        this.popupService.openEurotaxLeistungElektroInfoPopup(this.expertMode);
      }
    });
    this.hubraeume.subscribe(data => {
      preselectResetLookupValue(this.hubraum, data, (): void => { this.loadEurotax(); }, (): void => { }, (): void => { });
    });
    this.leistungen.subscribe(data => {
      preselectResetLookupValue(this.leistung, data, (): void => { this.loadEurotax(); }, (): void => { }, (): void => { });
    });
    this.doors.subscribe(data => {
      preselectResetLookupValue(this.tueren, data, (): void => { this.loadEurotax(); }, (): void => { }, (): void => { });
    });
    this.bauarten.subscribe(data => {
      preselectResetLookupValue(this.bauart, data, (): void => { this.loadEurotax(); }, (): void => { }, (): void => { });
    });
    this.eurotaxresults.subscribe(data => {
      this.dataSource.data = data;
      updateStickyColumnStyles(this.table, this.ngZone);
      if(data.length > 0 && !this.showMarkeModell) {
        if(data[0].typtxtFuelTYPeCd2 === '00100004'){
        this.popupService.openEurotaxLeistungElektroInfoPopup(this.expertMode);
        }
      }
    });
  }

  ngOnInit(): void {
    this.formGroup = this.fb.group({
      eingabeart: new UntypedFormControl(''),
      select: new UntypedFormControl(''),
      baujahr: new UntypedFormControl('', [Validators.required, Validators.pattern('^[0-9]*$'), Validators.min(1900), Validators.max(3000)]),
      marke: new UntypedFormControl('', CustomValidator.requiredLookupValue),
      model: new UntypedFormControl('', CustomValidator.requiredLookupValue),
      antriebsart: new UntypedFormControl(''),
      hubraum: new UntypedFormControl(''),
      leistung: new UntypedFormControl(''),
      tueren: new UntypedFormControl(''),
      bauart: new UntypedFormControl(''),
      natCode: new UntypedFormControl('', Validators.required)
    });
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    this.dataSource.sortingDataAccessor = (item, property) => {
      switch (property) {
        case 'HandelsBezeichnung': return item.typBezeichnung;
        case 'Baujahr': return Number(item.modellImportBeginn.substring(0, 4));
        case 'Antriebsart': return item.typtxtFuelTYPeCd2TXTTextLong;
        case 'Leistung': return item.leistungKw;
        case 'Listenpreis': return item.preisInkl;
        default: return item[property];
      }
    };
    this.sort.sortChange.subscribe(() => this.paginator.pageIndex = 0);
    merge(
      this.sort.sortChange,
      this.paginator.page
    ).subscribe(() => {
      updateStickyColumnStyles(this.table, this.ngZone);
    });
    this.onValueChanged();
    this.toggleEditMode();

    this.eingabeart.setValue(1);
  }
  get eingabeart() { return this.formGroup.get('eingabeart'); }
  get select() { return this.formGroup.get('select'); }
  get baujahr() { return this.formGroup.get('baujahr'); }
  get marke() { return this.formGroup.get('marke'); }
  get model() { return this.formGroup.get('model'); }
  get antriebsart() { return this.formGroup.get('antriebsart'); }
  get hubraum() { return this.formGroup.get('hubraum'); }
  get leistung() { return this.formGroup.get('leistung'); }
  get tueren() { return this.formGroup.get('tueren'); }
  get bauart() { return this.formGroup.get('bauart'); }
  get natCode() { return this.formGroup.get('natCode'); }

  get showNatcode() {
    return this.eingabeart.value && this.eingabeart.value === 1 ? true : false;
  }

  get showMarkeModell() {
    return this.eingabeart.value && this.eingabeart.value === 2 ? true : false;
  }

  get showHubraum() {
    return this.showMarkeModell && this.motorrad;
  }

  get showTuerenBauart() {
    return this.showMarkeModell && !this.motorrad;
  }

  get columnsToDisplay() { return this.motorrad ? this.displayedColumns : this.displayedColumns.filter(x => x !== 'Hubraum'); }

  toggleEditMode(): void {
    toggleEditMode(this.marke, this.showMarkeModell);
    toggleEditMode(this.model, this.showMarkeModell);
    toggleEditMode(this.antriebsart, this.showMarkeModell);
    toggleEditMode(this.hubraum, this.showHubraum);
    toggleEditMode(this.leistung, this.showMarkeModell);
    toggleEditMode(this.tueren, this.showTuerenBauart);
    toggleEditMode(this.bauart, this.showTuerenBauart);
    toggleEditMode(this.natCode, this.showNatcode);
  }

  loadMarken(): void {
    if (this.baujahr.valid) {
      this.eurotaxService.getMakes(this.motorrad ? 40 : 10, Number(this.baujahr.value)).subscribe(
        data => {
          this.marken.next(data);
        },
        () => {
          this.popupService.openErrorPopup(this.messageService.generateErrorMessage(ErrorMessageType.EurotaxService));
        }
      );
    }
  }

  validateModels(): boolean {
    return this.baujahr.valid && this.showNatcode ? this.natCode.valid : this.marke.valid ? true : false;
  }

  loadModels(modelsmarkebaujahr: boolean = false): void {
    if (this.validateModels()) {
      this.eurotaxService.getList(
        this.natCode.value,
        this.marke.value ? this.marke.value.Value : null,
        this.model.value ? this.model.value.Value : null,
        Number(this.baujahr.value),
        this.leistung.value ? Number(this.leistung.value.Id) : null,
        this.antriebsart.value ? this.antriebsart.value.Id : null,
        this.motorrad ? 40 : 10,
        this.tueren.value ? Number(this.tueren.value.Id) : null,
        this.bauart.value ? this.bauart.value.Id : null,
        this.hubraum.value ? Number(this.hubraum.value.Id) : null
      ).subscribe(
        data => {
          if (modelsmarkebaujahr) {
            this.modelsmarkebaujahr.next(data);
          }
          this.models.next(data);
        },
        () => {
          this.popupService.openErrorPopup(this.messageService.generateErrorMessage(ErrorMessageType.EurotaxService));
        }
      );
    }
  }

  loadEurotax() {
    if (this.validateModels() && (this.showNatcode ||
      this.model.valid && this.antriebsart.value && (this.showHubraum ? this.hubraum.value : this.leistung.value))) {
      this.eurotaxService.getList(
        this.natCode.value,
        this.marke.value ? this.marke.value.Value : null,
        this.model.value ? this.model.value.Value : null,
        Number(this.baujahr.value),
        this.leistung.value ? Number(this.leistung.value.Id) : null,
        this.antriebsart.value ? this.antriebsart.value.Id : null,
        this.motorrad ? 40 : 10,
        this.tueren.value ? Number(this.tueren.value.Id) : null,
        this.bauart.value ? this.bauart.value.Id : null,
        this.hubraum.value ? Number(this.hubraum.value.Id) : null
      ).subscribe(data => {
        this.eurotaxresults.next(data);
      },
      () => {
        this.popupService.openErrorPopup(this.messageService.generateErrorMessage(ErrorMessageType.EurotaxService));
      });
    }
  }

  onValueChanged(): void {
    this.select.valueChanges.subscribe(() => {
      this.dialogRef.close(this.select.value);
    });
    this.eingabeart.valueChanges.subscribe(() => {
      this.baujahr.reset(null);
      this.toggleEditMode();
      if (this.showMarkeModell) {
        this.antriebsartenSelected.next(null);
      }
    });
    this.baujahr.valueChanges
      .pipe(debounce(() => interval(300)))
      .subscribe(() => {
        if (this.showMarkeModell) {
          const options = { emitEvent: false };
          this.marke.setValue(null, options);
          this.model.setValue(null, options);
          this.antriebsart.setValue(null, options);
          this.leistung.setValue(null, options);
          this.hubraum.setValue(null, options);
          this.tueren.setValue(null, options);
          this.bauart.setValue(null, options);

          this.loadMarken();
          this.loadModels(true);
        } else {
          this.loadEurotax();
        }
      });
    this.marke.valueChanges
      .pipe(debounce(() => interval(300)))
      .subscribe(() => {
        const options = { emitEvent: false };
        this.model.setValue(null, options);
        this.antriebsart.setValue(null, options);
        this.leistung.setValue(null, options);
        this.hubraum.setValue(null, options);
        this.tueren.setValue(null, options);
        this.bauart.setValue(null, options);

        this.loadModels(true);
      });
    this.model.valueChanges
      .pipe(debounce(() => interval(300)))
      .subscribe(() => {
        if (this.model.valid) {
          const options = { emitEvent: false };
          this.antriebsart.setValue(null, options);
          this.leistung.setValue(null, options);
          this.hubraum.setValue(null, options);
          this.tueren.setValue(null, options);
          this.bauart.setValue(null, options);

          this.loadModels();
        }
      });
    this.antriebsart.valueChanges
      .pipe(debounce(() => interval(300)))
      .subscribe(() => {
        const options = { emitEvent: false };
        this.leistung.setValue(null, options);
        this.hubraum.setValue(null, options);
        this.tueren.setValue(null, options);
        this.bauart.setValue(null, options);

        this.loadModels();
        this.antriebsartenSelected.next(this.antriebsart.value);
      });
    this.hubraum.valueChanges
      .pipe(debounce(() => interval(300)))
      .subscribe(() => {
        const options = { emitEvent: false };
        this.leistung.setValue(null, options);
        this.tueren.setValue(null, options);
        this.bauart.setValue(null, options);

        this.loadModels();
        this.loadEurotax();
      });
    this.leistung.valueChanges
      .pipe(debounce(() => interval(300)))
      .subscribe(() => {
        const options = { emitEvent: false };
        this.tueren.setValue(null, options);
        this.bauart.setValue(null, options);

        this.loadModels();
        this.loadEurotax();
      });
    this.tueren.valueChanges
      .pipe(debounce(() => interval(300)))
      .subscribe(() => {
        const options = { emitEvent: false };
        this.bauart.setValue(null, options);

        this.loadModels();
        this.loadEurotax();
      });
    this.bauart.valueChanges
      .pipe(debounce(() => interval(300)))
      .subscribe(() => {
        this.loadModels();
        this.loadEurotax();
      });
    this.natCode.valueChanges
      .pipe(debounce(() => interval(300)))
      .subscribe(() => {
        this.loadEurotax();
      });
  }

  close() {
    this.dialogRef.close();
  }
}
